import React, { useMemo } from "react";

import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";

import { PICKUP_TYPE_KEYS } from "../utils";

import { ReactComponent as CheckIcon } from "../../../../../assets/icons/Supply & Pickup/check.svg";
import { ReactComponent as ClockIcon } from "../../../../../assets/icons/Supply & Pickup/clock.svg";
import { ReactComponent as FedexPickupIcon } from "../../../../../assets/icons/Supply & Pickup/FedEx_Pickup.svg";
import { ReactComponent as LocationIcon } from "../../../../../assets/icons/Supply & Pickup/location.svg";
import "./SelectPickupTypeModal.css";

const LOGISTICS = {
  COURIER: "Courier",
  FEDEX: "Fedex",
};

const PICKUP_OPTIONS = [
  {
    key: PICKUP_TYPE_KEYS.FEDEX_PICKUP,
    icon: FedexPickupIcon,
    title: "FedEx Pickup",
    subtitle: "Scheduled at your location",
    description: "We'll arrange a FedEx driver to collect your boxes directly from your facility.",
    features: [
      "System-generated shipping label",
      "Real-time status tracking in dashboard",
      "Confirmed pickup window",
    ],
  },
  {
    key: PICKUP_TYPE_KEYS.LOCAL_COURIER,
    icon: ClockIcon,
    title: "Local Courier",
    subtitle: "Local partner pickup",
    description: (
      <>
        Our local courier partners will pick up your boxes. Orders must be placed before
        {" "}
        <strong>7AM for same-day pickup.</strong>
      </>
    ),
    features: [
      "Order before 7:00 AM for same-day",
      "Local partner network",
      "Cost-effective for nearby areas",
    ],
  },
  {
    key: PICKUP_TYPE_KEYS.FEDEX_DROPOFF,
    icon: LocationIcon,
    title: "FedEx Drop-Off",
    subtitle: "Drop at a FedEx location",
    description: "Print your label and drop off your boxes at any nearby FedEx location at your convenience.",
    features: [
      "Printable label generated instantly",
      "Drop off at 3,000+ FedEx locations",
      "Flexible schedule — go anytime",
    ],
  },
];

const getAllowedPickupKeys = (logistics) => {
  const normalized = typeof logistics === "string" ? logistics.trim().toLowerCase() : "";
  if (normalized === LOGISTICS.COURIER.toLowerCase()) {
    return new Set(PICKUP_OPTIONS.map((option) => option.key));
  }
  if (normalized === LOGISTICS.FEDEX.toLowerCase()) {
    return new Set([
      PICKUP_TYPE_KEYS.FEDEX_PICKUP,
      PICKUP_TYPE_KEYS.FEDEX_DROPOFF,
    ]);
  }
  return new Set([PICKUP_TYPE_KEYS.FEDEX_DROPOFF]);
};

const SelectPickupTypeModal = ({
  open, onCancel, onSelect, logistics = null,
}) => {
  const visibleOptions = useMemo(() => {
    const allowedKeys = getAllowedPickupKeys(logistics);
    return PICKUP_OPTIONS.filter((option) => allowedKeys.has(option.key));
  }, [logistics]);

  let modalWidth = 1120;
  let modalCountClass = "select-pickup-modal--count-3";
  if (visibleOptions.length <= 1) {
    modalWidth = 440;
    modalCountClass = "select-pickup-modal--count-1";
  } else if (visibleOptions.length === 2) {
    modalWidth = 760;
    modalCountClass = "select-pickup-modal--count-2";
  }

  return (
    <Modal
      centered
      className={`select-pickup-modal ${modalCountClass}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={modalWidth}
      title={(
        <div className="select-pickup-modal-header">
          <div className="select-pickup-modal-title">Create Pickup Request</div>
          <div className="select-pickup-modal-subtitle">Choose how you&apos;d like to ship your samples</div>
        </div>
      )}
    >
      <div className="select-pickup-modal-body">
        <div className={`select-pickup-options select-pickup-options--count-${visibleOptions.length}`}>
          {visibleOptions.map((option) => {
            const Icon = option.icon;
            return (
              <div key={option.key} className="select-pickup-card">
                <div className="select-pickup-card-top">
                  <div className="select-pickup-card-icon">
                    <Icon className="select-pickup-option-icon" />
                  </div>
                  {/* <div className="select-pickup-premium-badge">
                    <StarIcon className="select-pickup-premium-star" />
                    Premium Plan — free
                  </div> */}
                </div>

                <div className="select-pickup-card-title">{option.title}</div>
                <div className="select-pickup-card-subtitle">{option.subtitle}</div>
                <p className="select-pickup-card-description">{option.description}</p>

                <ul className="select-pickup-card-features">
                  {option.features.map((feature) => (
                    <li key={feature}>
                      <CheckIcon className="select-pickup-feature-check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  type="default"
                  className="select-pickup-card-action"
                  onClick={() => onSelect({
                    pickupTypeKey: option.key,
                    pickupTypeLabel: option.title,
                    phase: "select-submissions",
                  })}
                >
                  Select
                  <ArrowRightOutlined />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default SelectPickupTypeModal;
